import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import cx from "classnames";
import _ from "lodash";

import { BannerView } from "src/components/banner";
import { ScrollableContainer } from "src/components/scroller";
import { TabBar } from "src/components/tabbar";
import { PodcastMenuBar } from "../components/menu";
import { PodcastNotFound } from "src/pages/coaching/components/notFound";
import { CourseLearners } from "../../courses/edit/components/learners";
import {
  PodcastTranscriptRowPlaceholder,
  PodcastTranscriptsContainer,
} from "src/pages/coaching/components/podcast/components/transcripts";
import { PodcastContext, PodcastContextProps } from "../edit/context";
import { Podcast } from "src/types/coaching/podcast";
import { PODCAST_TYPES } from "src/constants/podcast";
import { FileService } from "src/utils/file.service";
import userRoleService from "src/utils/userRoles.service";
import {
  getOverviewBackLabel,
  getManageNavigationRootFrom,
  navigateOverviewBack,
  resolveOverviewBackPath,
  COACHING_MANAGE_PATH,
  COACHING_REPORTS_PATH,
} from "src/pages/coaching/utils/manageNavigation";
import {
  fetchPodcastTranscription,
  fetchPublishedPodcast,
  invalidatePublishedPodcast,
} from "src/redux/actions/coaching/podcast";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import styles from "./view.module.scss";

interface MetricsCardProps {
  name: string;
  value?: string;
  subTitle?: string;
}

const MetricsCard: React.FC<MetricsCardProps> = ({ name, value, subTitle }) => (
  <div className={styles.infoCard}>
    <div className={styles.metrics}>
      <span className={styles.name}>{name}</span>
      <span className={styles.value}>{value}</span>
      {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
    </div>
  </div>
);

const LeftArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.leftArrow)}>
    <i className="paperflite-arrow-up" />
  </div>
);

const RightArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.rightArrow)}>
    <i className="paperflite-arrow-up" />
  </div>
);

const OverviewTranscripts: React.FC<{ podcastId: string }> = ({ podcastId }) => {
  const { loading, transcripts } = useSelector((state: any) => {
    const loading = _.get(
      state.coaching.podcasts,
      `podcast[${podcastId}].transcription.loading`,
      false,
    );
    const transcripts =
      _.get(state.coaching.podcasts, `podcast[${podcastId}].transcription.item`) ||
      [];

    return { loading, transcripts };
  });

  const emptyTranscripts = _.size(transcripts) === 0 && !loading;

  if (emptyTranscripts) {
    return (
      <div className={styles.transcriptsContainer}>
        <div className={styles.emptyTranscripts}>
          {_.times(5, (index) => (
            <PodcastTranscriptRowPlaceholder key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.transcriptsContainer}>
      <PodcastTranscriptsContainer loading={loading} transcripts={transcripts} />
    </div>
  );
};

export const PodcastOverviewPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const rootFrom = getManageNavigationRootFrom(location.state);
  const params = useParams();
  const id = params.id;

  const podcast: Podcast | undefined = useSelector((state: any) =>
    _.get(state.coaching.podcasts, `podcast[${id}].item`),
  );

  const loading = useSelector((state: any) =>
    _.get(state.coaching.podcasts, `podcast[${id}].loading`),
  );

  const podcastErrorCode = useSelector((state: any) =>
    _.get(state.coaching.podcasts, `podcast[${id}].error.errors[0].code`),
  );

  const [activeTab, setActiveTab] = useState(0);

  const name = useMemo(() => _.get(podcast, "name") || "", [podcast]);
  const description = useMemo(
    () => _.get(podcast, "description") || "",
    [podcast],
  );
  const banner = useMemo(() => _.get(podcast, "banner"), [podcast]);
  const topics = useMemo(() => _.get(podcast, "topics") || [], [podcast]);
  const skills = useMemo(() => _.get(podcast, "skills") || [], [podcast]);
  const transcriptUrl = useMemo(
    () => _.get(podcast, "transcriptUrl", null),
    [podcast],
  );

  const podcastDuration = useMemo(() => {
    const duration = _.get(podcast, "audio.duration") || 0;
    if (duration > 0) {
      return FileService.formatDuration(duration, "long");
    }
    return null;
  }, [podcast]);

  const podcastTypeLabel = useMemo(() => {
    const type = _.get(podcast, "type");
    return PODCAST_TYPES.find((item) => item.id === type)?.value || type || "";
  }, [podcast]);

  const completedLearners = useMemo(
    () => _.get(podcast, "completedLearners") || 0,
    [podcast],
  );
  const totalLearners = useMemo(
    () => _.get(podcast, "totalLearners") || 0,
    [podcast],
  );
  const averageTimeSpent = useMemo(
    () => _.get(podcast, "averageTimeSpent") || 0,
    [podcast],
  );
  const totalTimeSpent = useMemo(
    () => _.get(podcast, "totalTimeSpent") || 0,
    [podcast],
  );

  const completionInfo = useMemo(() => {
    const rate =
      totalLearners > 0
        ? Math.round((completedLearners / totalLearners) * 100)
        : 0;
    const text = `${completedLearners} of ${totalLearners} learners have completed`;
    return { rate, text };
  }, [completedLearners, totalLearners]);

  const resolvedBackPath = useMemo(
    () => resolveOverviewBackPath(rootFrom),
    [rootFrom],
  );

  const backLabel = useMemo(
    () => getOverviewBackLabel(rootFrom),
    [rootFrom],
  );

  const contextState: PodcastContextProps = useMemo(() => ({ id }), [id]);

  const onBackClick = () => {
    const fallback =
      userRoleService.canAccessCoachingReport() &&
      !userRoleService.canCreateCourse()
        ? COACHING_REPORTS_PATH
        : COACHING_MANAGE_PATH;

    navigateOverviewBack(navigate, location.state, fallback);
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(invalidatePublishedPodcast(id));
    dispatch(fetchPublishedPodcast(id));
    dispatch(
      fetchCourseLearners({
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      }),
    );
  }, [id]);

  useEffect(() => {
    if (!podcast?.id || !transcriptUrl) {
      return;
    }

    dispatch(fetchPodcastTranscription(podcast.id));
  }, [podcast?.id, transcriptUrl, dispatch]);

  if (podcastErrorCode === 10007) {
    return (
      <PodcastNotFound
        onBack={() => navigate(resolvedBackPath || "/coaching/manage")}
      />
    );
  }

  if (!podcast || (loading && !podcast.publishedBy)) {
    return <div className={styles.loading}>Loading podcast...</div>;
  }

  return (
    <PodcastContext.Provider value={contextState}>
      <div className={cx(styles.podcastContainer, styles.viewMode)}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div className={styles.back} onClick={onBackClick}>
              <i className="paperflite-left-arrow" />
              <span>{backLabel}</span>
            </div>
          </div>
          <div className={styles.menubar}>
            <PodcastMenuBar
              podcast={podcast}
              menuState={{ primaryAction: null, secondaryAction: null }}
              onMenuStateChange={() => {}}
              mode="viewer"
            />
          </div>
        </div>

        <div className={styles.editContainer}>
          <div className={styles.top}>
            <div className={styles.left}>
              <div className={styles.details}>
                <div className={styles.nameRow}>
                  <div className={cx(styles.field, styles.thumbnail)}>
                    <div className={styles.inner}>
                      <BannerView
                        banner={banner as any}
                        defaultBanner="/images/assets/podcastThumbnail.jpg"
                        overlay={false}
                      />
                    </div>
                  </div>
                  <div className={cx(styles.field, styles.name)}>
                    <div className={styles.input}>{name}</div>
                  </div>
                </div>

                <div className={styles.metadataRow}>
                  <div className={cx(styles.field, styles.configurations)}>
                    {podcastDuration && (
                      <div className={styles.item}>
                        <i className={cx(styles.duration, "paperflite-clock")} />
                        <span>{podcastDuration}</span>
                      </div>
                    )}
                    {podcastTypeLabel && (
                      <div className={styles.item}>
                        <i
                          className={cx(styles.speakers, "paperflite-speedomenter")}
                        />
                        <span>Type: {podcastTypeLabel}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.editDetailsContainer}>
                <TabBar
                  className={cx(styles.tabs)}
                  gap={32}
                  tabSize="equal"
                  active={activeTab}
                  onSelectionChange={(tab) => setActiveTab(tab)}
                >
                  <div
                    className={cx(styles.tab, { [styles.active]: activeTab === 0 })}
                  >
                    Details
                  </div>
                  <div
                    className={cx(styles.tab, { [styles.active]: activeTab === 1 })}
                  >
                    Transcript
                  </div>
                </TabBar>
                <div className={styles.body}>
                  {activeTab === 0 && (
                    <div className={styles.topicsContainer}>
                      {description && (
                        <div
                          className={styles.readonlyDescription}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(description),
                          }}
                        />
                      )}
                      {_.size(topics) > 0 && (
                        <div className={styles.topics}>
                          <div className={styles.title}>
                            here’s the list of topics, we have covered in this
                            podcast
                          </div>
                          <div className={styles.topicsContent}>
                            {topics.map((topic: { name?: string }, index: number) => (
                              <div key={index} className={styles.topic}>
                                <span className={styles.index}>
                                  {String(index + 1).padStart(2, "0")}
                                </span>
                                <span className={styles.separator}>/</span>
                                <span className={styles.name}>{topic.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {activeTab === 1 && id && <OverviewTranscripts podcastId={id} />}
                </div>
              </div>
            </div>

            <div className={styles.right}>
              <div className={styles.inner}>
                <div className={styles.detailsPane}>
                  <ScrollableContainer
                    distance={300}
                    leftArrowComponent={LeftArrow}
                    rightArrowComponent={RightArrow}
                  >
                    <div className={styles.metrics}>
                      <MetricsCard
                        name="completion rate"
                        value={`${completionInfo.rate}%`}
                        subTitle={completionInfo.text}
                      />
                      <MetricsCard
                        name="total listen time"
                        value={FileService.formatDuration(totalTimeSpent, "short")}
                      />
                      <MetricsCard
                        name="avg listen time"
                        value={FileService.formatDuration(
                          averageTimeSpent,
                          "short",
                        )}
                      />
                    </div>
                  </ScrollableContainer>

                  <div className={styles.skillsContainer}>
                    <p className={styles.heading}>
                      skills learners will gain ({skills.length})
                    </p>
                    <div className={styles.skills}>
                      {skills.map((skill: { id?: string; name?: string }) => (
                        <div key={skill.id} className={styles.skill}>
                          <div className={styles.name}>{skill.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.courseLearnersContainer}>
                    <CourseLearners
                      id={podcast.id!}
                      course={podcast}
                      navigationFrom={location.pathname}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PodcastContext.Provider>
  );
};
